import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles } from "lucide-react";

/**
 * Demo test flow — no login, no trainer needed.
 * Same UX as PublicTest but doesn't require a token.
 */
export default function DemoTest() {
  const [questions, setQuestions] = useState([]);
  const [name, setName] = useState("");
  const [step, setStep] = useState(-2); // -2 = name input, -1 = welcome, 0..n-1 = questions
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta/questions");
        setQuestions(data.questions);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const total = questions.length;

  const selectAnswer = (qid, optId) => {
    setAnswers((a) => ({ ...a, [qid]: optId }));
    setTimeout(() => {
      if (step < total - 1) setStep(step + 1);
    }, 250);
  };

  const submit = async () => {
    if (Object.keys(answers).length < total) {
      toast.error(`Bitte alle ${total} Fragen beantworten.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/demo/submit", { name, answers });
      sessionStorage.setItem(
        "fmp_demo_result",
        JSON.stringify({ analysis: data.analysis, name: data.name, demo_id: data.demo_id })
      );
      nav(`/demo/${data.demo_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Step -2: name
  if (step === -2) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / DEMO-TEST · KEIN KONTO NÖTIG
          </div>
          <h1 className="font-heading font-black text-5xl leading-none tracking-tight mb-6">
            Probier den Test aus.
          </h1>
          <p className="text-white/70 mb-8 leading-relaxed">
            Der Demo-Test ist identisch mit dem Trainer-Test. Nach 10–12 Minuten
            erhältst du dein FMP-Profil inkl. Radar, Spielertyp und
            PDF-Download.
          </p>
          <div className="space-y-3">
            <input
              data-testid="demo-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dein Name"
              className="w-full bg-[#111214] border border-white/10 rounded-md px-4 py-3.5 text-white focus:border-[#CCFF00] outline-none text-lg"
            />
            <button
              data-testid="demo-start-btn"
              onClick={() => name.trim() && setStep(-1)}
              disabled={!name.trim() || questions.length === 0}
              className="btn-primary w-full justify-center disabled:opacity-40"
            >
              Weiter
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step -1: welcome
  if (step === -1) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-16">
        <div className="max-w-2xl w-full">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / FOOTBALL MOTIVATION PROFILE
          </div>
          <h1 className="font-heading font-black text-5xl leading-none tracking-tight mb-6">
            Hallo <span className="text-[#CCFF00]">{name}</span>.
          </h1>
          <div className="space-y-4 mb-10 text-white/80">
            <p>
              Du beantwortest gleich <b>60 realistische Situationen</b> aus dem
              Fußballalltag. Es gibt <b>keine richtigen oder falschen Antworten</b>.
            </p>
            <ul className="space-y-2 text-sm text-white/60">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                Antworte spontan – dein erster Gedanke ist meistens der ehrlichste.
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                Dauer: ca. 10–12 Minuten.
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0 mt-0.5" />
                Am Ende: dein Spielertyp + Radar + PDF.
              </li>
            </ul>
          </div>
          <button
            data-testid="demo-begin-btn"
            onClick={() => setStep(0)}
            className="btn-primary"
          >
            Test starten
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  const q = questions[step];
  if (!q) return null;
  const isLast = step === total - 1;
  const answered = answers[q.id] !== undefined;
  const progress = Math.round(((step + 1) / total) * 100);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <div className="sticky top-0 z-10 bg-[#050505]/95 backdrop-blur-md border-b border-white/5">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="font-display tracking-widest text-white/60">
              Frage {step + 1} / {total}
            </span>
            <span className="font-display tracking-widest text-[#CCFF00]">
              {progress}%
            </span>
          </div>
          <div className="progress-bar">
            <div className="fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center px-6 py-12">
        <div className="mx-auto max-w-3xl w-full">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-4">
            / SITUATION {String(step + 1).padStart(2, "0")}
          </div>
          <h2
            data-testid={`demo-question-text-${step}`}
            className="font-heading font-bold text-2xl md:text-3xl leading-snug mb-8"
          >
            {q.text}
          </h2>
          <div className="space-y-3 mb-8">
            {q.options.map((opt) => (
              <button
                key={opt.id}
                data-testid={`demo-option-${step}-${opt.id}`}
                onClick={() => selectAnswer(q.id, opt.id)}
                className={`option-card ${
                  answers[q.id] === opt.id ? "selected" : ""
                }`}
              >
                <span className="bullet" />
                <span className="text-base leading-snug">{opt.text}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <button
              onClick={() => step > 0 && setStep(step - 1)}
              disabled={step === 0}
              data-testid="demo-prev-btn"
              className="btn-ghost !py-2 !px-4 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>
            {isLast ? (
              <button
                data-testid="demo-submit-btn"
                onClick={submit}
                disabled={!answered || submitting}
                className="btn-primary disabled:opacity-40"
              >
                {submitting ? "Wird ausgewertet …" : "Test abschließen"}
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                data-testid="demo-next-btn"
                onClick={() => setStep(step + 1)}
                disabled={!answered}
                className="btn-primary disabled:opacity-40"
              >
                Weiter
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
